/* eslint-disable max-statements */

import { Badge, Group, LoadingOverlay, Tabs } from '@mantine/core';
import { isEqual, sortBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BatchTypeEnum, Declaration, DeclarationStatus, EnvironmentCountries, FilterRisksValues, IDeclarationsCriteria } from '@e-origin/shared';

import { BackButton, SearchBox, Table } from '@components';
import { DeclarationsTabs } from '@enums';
import { BatchView } from '@features/batches';
import { ContentLayout } from '@features/layout';
import { defaultCommentCriteria, useComments } from '@hooks/comment';
import { defaultDeclarationsCriteria, getInitialCriteria, useDeclarations, useDeclarationsMeta } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { removeUndefined, STORAGE_KEYS } from '@utils';
import { getStoredCriteria, setStoredCriteria } from '../../../../criteria.caching';
import { useDeclarationTableColumns } from './DeclarationTable';

export const DeclarationList = () => {
  const { data: settings, isLoading: isSettingsLoading } = useSettings();

  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const navigate = useNavigate();

  const selectedRow = useRef<Declaration>(null);

  const noQueryParams = !queryParams.current.get('batchId') && !queryParams.current.get('lrn');
  const batchName = queryParams.current.get('batchName');
  const criteria = useMemo(() => {
    return noQueryParams ? getInitialCriteria() : defaultDeclarationsCriteria;
  }, []);

  const [filters, setFilters] = useState<IDeclarationsCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<IDeclarationsCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<IDeclarationsCriteria['pagination']>(criteria.pagination);

  const { data: declarationsList, isLoading } = useDeclarations({ filters, sorting, pagination });
  const firstDeclaration = declarationsList?.[0];

  const declarationIds = useMemo(() => declarationsList?.map((declaration) => declaration._id) || [], [declarationsList]);
  const { data: { list: comments = [] } = {} } = useComments(
    {
      filters: { filterByEntityId: declarationIds, filterByEntityParentId: [] },
      sorting: defaultCommentCriteria.sorting,
      pagination: defaultCommentCriteria.pagination,
    },
    declarationIds.length > 0,
  );

  const { data: { total: totalDeclarationsCount, statusCounts } = {} } = useDeclarationsMeta({ filters, sorting, pagination });

  const setDeclarationsFilters = (updatedFilters: Partial<IDeclarationsCriteria['filters']>) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...updatedFilters,
    });

    setFilters(mergedFilters);
  };

  const onSelectedRow = (row) => {
    selectedRow.current = row;
  };

  const criteriaInitialized = useRef<boolean>(false);
  const configLoaded = useRef<boolean>(false);

  const [batchView, setBatchView] = useState((getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) || BatchTypeEnum.HIGH_VALUES);

  const columns = useDeclarationTableColumns({
    onSelectedRow,
    filters,
    batchId: queryParams.current.get('batchId'),
    listTab: queryParams.current.get('tab'),
    lrn: queryParams.current.get('lrn'),
    batchView,
    comments,
  });

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    if (noQueryParams) {
      setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_DECLARATIONS_CRITERIA, { filters, sorting, pagination: { page, size } });
    }
    setPagination({ page, size });
  };

  const canDisplayBeGateTab = useMemo(
    () => batchView === BatchTypeEnum.HIGH_VALUES && settings?.country === EnvironmentCountries.BE && !settings?.isForwarder,
    [batchView, settings],
  );

  const tabs = useMemo(
    () => [
      { id: DeclarationsTabs.RISK_ANALYSIS, label: 'Risk analysis', value: DeclarationStatus.ANALYSIS, 'data-pw': 'declarations-risk-analysis-tab' },
      ...(canDisplayBeGateTab
        ? [
            {
              id: DeclarationsTabs.BE_GATE,
              label: 'BE-Gate',
              value: [DeclarationStatus.BEGATE_SENT, DeclarationStatus.BEGATE_NOT_RELEASED, DeclarationStatus.BEGATE_RELEASED],
              'data-pw': 'declarations-be-gate-tab',
            },
          ]
        : []),
      { id: DeclarationsTabs.NOT_SENT, label: 'Not sent', value: DeclarationStatus.NOT_SENT, 'data-pw': 'declarations-not-sent-tab' },
      { id: DeclarationsTabs.SENDING, label: 'Sending', value: DeclarationStatus.SENDING, 'data-pw': 'declarations-sending-tab' },
      { id: DeclarationsTabs.SENT, label: 'Sent', value: DeclarationStatus.SENT, 'data-pw': 'declarations-sent-tab' },
      {
        id: DeclarationsTabs.VALIDATION_IN_PROGRESS,
        label: 'Validation in progress',
        value: DeclarationStatus.ACCEPTED,
        'data-pw': 'declarations-validation-in-progress-tab',
      },
      { id: DeclarationsTabs.REJECTED, label: 'Rejected', value: DeclarationStatus.REJECTED, 'data-pw': 'declarations-rejected-tab' },
      { id: DeclarationsTabs.IN_CONTROL, label: 'In control', value: DeclarationStatus.IN_CONTROL, 'data-pw': 'declarations-in-control-tab' },
      { id: DeclarationsTabs.NOT_RELEASED, label: 'Not released', value: DeclarationStatus.NOT_RELEASED, 'data-pw': 'declarations-not-released-tab' },
      { id: DeclarationsTabs.RELEASED, label: 'Released', value: DeclarationStatus.RELEASED, 'data-pw': 'declarations-released-tab' },
      { id: DeclarationsTabs.INVALIDATED, label: 'Invalidated', value: DeclarationStatus.INVALIDATED, 'data-pw': 'declarations-invalidated-tab' },
      ...(batchView === BatchTypeEnum.HIGH_VALUES_EXPORT
        ? [{ id: DeclarationsTabs.EXPORTED, label: 'Exported', value: DeclarationStatus.EXPORTED, 'data-pw': 'declarations-exported-tab' }]
        : []),
    ],
    [batchView, canDisplayBeGateTab],
  );

  const getTabCount = (value: DeclarationStatus | DeclarationStatus[]) => [value].flat().reduce((sum, status) => sum + (statusCounts?.[status] ?? 0), 0);

  const getDeclarationsStatus = (tab: DeclarationsTabs) => tabs.find((t) => t.id === tab)?.value;

  const getDeclarationTab = (status: DeclarationStatus | DeclarationStatus[]) =>
    tabs.find((t) => (Array.isArray(t.value) ? isEqual(sortBy(status), sortBy(t.value)) || t.value.includes(status as DeclarationStatus) : t.value === status))
      ?.id;

  const currentTab = useRef<DeclarationsTabs>(getDeclarationTab(queryParams.current.get('tab') as DeclarationStatus | DeclarationStatus[]));

  const tabHandler = (tab: DeclarationsTabs) => {
    const foundTab = tabs.find((item) => item.id === tab);
    if (!foundTab) return;

    currentTab.current = foundTab.id;

    queryParams.current.set('tab', getDeclarationsStatus(currentTab.current).toString());
    navigate({ search: queryParams.current.toString() }, { replace: true });

    setDeclarationsFilters({ status: getDeclarationsStatus(currentTab.current) });
  };

  useEffect(() => {
    if (settings && !configLoaded.current) {
      configLoaded.current = true;
    }
  }, [settings]);

  useEffect(() => {
    if (!criteriaInitialized.current || !configLoaded.current) {
      return;
    }

    if (noQueryParams) {
      setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_DECLARATIONS_CRITERIA, { filters, sorting, pagination });
    }
  }, [filters, sorting, criteriaInitialized.current]);

  useEffect(() => {
    if (!routeLocation || !configLoaded.current) {
      return;
    }

    const tabFromUrl = queryParams.current.get('tab') as DeclarationStatus;
    const defaultTab = DeclarationsTabs.RISK_ANALYSIS;

    if ([...queryParams.current].length) {
      setDeclarationsFilters({
        searchQuery: filters.searchQuery || '',
        status: getDeclarationsStatus(getDeclarationTab(tabFromUrl)) || filters?.status || DeclarationStatus.ANALYSIS,
        riskValues: (queryParams.current.get('riskValues') as FilterRisksValues) || undefined,
        group: (getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) === BatchTypeEnum.LOW_VALUES ? 'H7' : 'H1',
        isExport: (getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) === BatchTypeEnum.HIGH_VALUES_EXPORT,
        batchId: queryParams.current.get('batchId') || undefined,
        lrn: queryParams.current.get('lrn') || undefined,
        fetchArchived: queryParams.current.get('archived') === 'true' ? true : undefined,
      });
    } else {
      setDeclarationsFilters({
        group: (getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) === BatchTypeEnum.LOW_VALUES ? 'H7' : 'H1',
        isExport: (getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) === BatchTypeEnum.HIGH_VALUES_EXPORT,
        lrn: undefined,
        status: getDeclarationsStatus(defaultTab),
      });
    }
    currentTab.current = tabFromUrl ? getDeclarationTab(tabFromUrl) : defaultTab;

    criteriaInitialized.current = true;
  }, [configLoaded.current, routeLocation, isSettingsLoading]);

  const searchDeclarations = (searchQuery: string) => {
    setDeclarationsFilters({ searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleBatchView = (value: BatchTypeEnum) => {
    setBatchView(value);
    setDeclarationsFilters({
      group: [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(value) ? 'H1' : 'H7',
      isExport: value === BatchTypeEnum.HIGH_VALUES_EXPORT || undefined,
    });
  };

  if (!declarationsList) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  const getTitleSuffix = (): string => {
    if (noQueryParams) return '';

    if (!batchName && batchView !== BatchTypeEnum.LOW_VALUES && firstDeclaration?.consignment?.referenceNumberUCR) {
      return `(UCR: ${firstDeclaration.consignment.referenceNumberUCR})`;
    }
    return `Batch: ${batchName}`;
  };

  return (
    <ContentLayout>
      {!noQueryParams && (
        <Group ml={24}>
          <BackButton backPath={-1} replace></BackButton>
        </Group>
      )}
      <ContentLayout.Header>
        <ContentLayout.Title data-pw="declaration-lines-title">Declaration lines {getTitleSuffix()}</ContentLayout.Title>
        <Group>
          <BatchView value={batchView} onChange={handleBatchView} />
          <SearchBox handleSearch={searchDeclarations} value={filters?.searchQuery} placeholder="Search by exporter, LRN, AWB, UCR" />
        </Group>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Tabs value={currentTab.current} onChange={(value) => tabHandler(value as DeclarationsTabs)}>
          <Tabs.List>
            {tabs.map((tab) => {
              const count = getTabCount(tab.value);
              return (
                <Tabs.Tab
                  key={tab.id}
                  value={tab.id}
                  data-pw={tab['data-pw']}
                  c={count ? undefined : 'dimmed'}
                  styles={{ tabSection: { marginInlineStart: 4 } }}
                  p="sm"
                  rightSection={
                    count > 0 && (
                      <Badge size="xs" variant="light" data-pw={`${tab['data-pw']}-count`}>
                        {count}
                      </Badge>
                    )
                  }
                >
                  {tab.label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={declarationsList ?? []}
          total={totalDeclarationsCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
